import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { resolveJoinCode, joinSession, type JoinResolve } from "./api";
import { useSessionSocket } from "./useSessionSocket";
import type { ChatMessage, ServerMessage } from "@shared/types";
import { MATURITY_SCALE as SCALE } from "./scale";

type Phase = "code" | "intro" | "polling" | "chat" | "error";

const AVATAR_COLORS = ["#7C3AED", "#0078d4", "#00b4d8", "#f59e0b", "#10b981", "#e11d48", "#8b5cf6"];
function colorFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}
function clock(iso: string): string {
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${ap}`;
}

export function Participant() {
  const { code: codeParam } = useParams();
  const [code, setCode] = useState(codeParam ?? "");
  const [phase, setPhase] = useState<Phase>(codeParam ? "intro" : "code");
  const [resolved, setResolved] = useState<JoinResolve | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [qIdx, setQIdx] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [err, setErr] = useState("");
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const heartbeat = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // Polling is anonymous — chat author is a generated handle (matches the server).
  const myAuthor = useMemo(
    () => (participantId ? `Participant ${participantId.slice(0, 4)}` : ""),
    [participantId]
  );

  const { send } = useSessionSocket(resolved?.sessionId ?? null, {
    onMessage: (msg: ServerMessage) => {
      if (msg.type === "state_snapshot") setChat([...msg.snapshot.chat].reverse());
      else if (msg.type === "chat_added") setChat((c) => (c.some((m) => m.id === msg.message.id) ? c : [...c, msg.message]));
    },
  });

  useEffect(() => {
    if (codeParam) void doResolve(codeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  useEffect(() => {
    if (participantId) {
      send({ type: "participant_hello", participantId });
      heartbeat.current = setInterval(() => send({ type: "presence_ping", participantId }), 15000);
    }
    return () => { if (heartbeat.current) clearInterval(heartbeat.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  // Keep newest message in view (chat is oldest-top, newest-bottom).
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, phase]);

  async function doResolve(c: string) {
    setErr("");
    try {
      setResolved(await resolveJoinCode(c));
      setPhase("intro");
    } catch {
      setErr("That code didn't match a live session.");
      setPhase("code");
    }
  }

  async function join() {
    if (!resolved) return;
    try {
      const p = await joinSession(resolved.sessionId, {}); // anonymous
      setParticipantId(p.id);
      setPhase("polling");
    } catch {
      setErr("Could not join — try again.");
    }
  }

  function submit(score: number) {
    if (!resolved || !participantId) return;
    const q = resolved.questions[qIdx];
    setSelected(score);
    send({ type: "submit_response", participantId, vectorIndex: q.vectorIndex, questionId: q.id, score });
    setTimeout(() => {
      if (qIdx < resolved.questions.length - 1) {
        setQIdx((i) => i + 1);
        setSelected(null);
      } else {
        setPhase("chat");
      }
    }, 320);
  }

  function sendChat() {
    const text = chatInput.trim();
    if (!text || !participantId) return;
    send({ type: "chat_message", participantId, text });
    setChatInput("");
  }

  // ── CODE / INTRO / POLLING use a centered card shell ───────────────────────
  const field: React.CSSProperties = {
    width: "100%",
    padding: "14px 16px",
    border: "1.5px solid var(--border)",
    borderRadius: "var(--radius-md)",
    fontSize: 16, // >=16px prevents iOS zoom-on-focus
    color: "var(--fg-inv)",
    background: "var(--surface)",
    fontFamily: "var(--font)",
  };

  if (phase === "code" || phase === "intro" || phase === "error") {
    return (
      <div className="pt-shell">
        <div className="pt-card">
          {phase === "code" && (
            <>
              <img src="/brand/ey-mark-dark.svg" alt="EY" style={{ height: 30 }} />
              <h2 className="pt-title">Join the workshop</h2>
              <p className="pt-sub">Enter the 4-digit code shown on the facilitator's screen.</p>
              <input
                style={{ ...field, fontFamily: "var(--font-mono)", fontSize: 28, letterSpacing: "0.3em", textAlign: "center" }}
                value={code}
                maxLength={4}
                inputMode="numeric"
                placeholder="0000"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
              {err && <div className="pt-err">{err}</div>}
              <button className="pt-btn" onClick={() => doResolve(code)} disabled={code.length < 4}>Continue →</button>
            </>
          )}
          {phase === "intro" && resolved && (
            <>
              <div className="pt-eyebrow">{resolved.session.client}</div>
              <h2 className="pt-title">{resolved.session.title}</h2>
              <p className="pt-sub">You're in. Your responses are anonymous — rate each factor on the maturity scale.</p>
              {err && <div className="pt-err">{err}</div>}
              <button className="pt-btn" onClick={join}>Start polling →</button>
            </>
          )}
          {phase === "error" && <div className="spinner" />}
        </div>
      </div>
    );
  }

  if (phase === "polling" && resolved) {
    const q = resolved.questions[qIdx];
    const total = resolved.questions.length;
    return (
      <div className="pt-shell">
        <div className="pt-card">
          <div className="polling-progress-bar" style={{ margin: 0, width: "100%" }}>
            <div className="polling-progress-fill" style={{ width: `${((qIdx + 1) / total) * 100}%` }} />
          </div>
          <div className="pt-qcount">Question {qIdx + 1} of {total}</div>
          <div className="pt-question">{q.text}</div>
          <div className="pt-factor">{q.factor}</div>
          <div className="pt-scale-hint">Pick the maturity stage that best fits today.</div>
          <div className="pt-scale">
            {SCALE.map((s) => (
              <button
                key={s.val}
                className={`pt-mat-opt ${selected === s.val ? "selected" : ""}`}
                style={{ ["--c" as string]: s.c } as React.CSSProperties}
                onClick={() => submit(s.val)}
                disabled={selected !== null}
              >
                <span className="pt-mat-num">{s.val}</span>
                <span className="pt-mat-icon">{s.icon}</span>
                <span className="pt-mat-text">
                  <span className="pt-mat-label">{s.label}</span>
                  <span className="pt-mat-blurb">{s.blurb}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── CHAT: full-screen mobile chat app ──────────────────────────────────────
  return (
    <div className="pt-chat">
      <header className="pt-chat-header">
        <div className="pt-chat-header-main">
          <div className="pt-chat-title">{resolved?.session.client || "Workshop"} · Discussion</div>
          <div className="pt-chat-sub"><span className="live-dot" /> Live · your messages show on the main screen</div>
        </div>
      </header>

      <div className="pt-chat-scroll">
        <div className="pt-chat-thankyou">
          <div className="pt-check">✓</div>
          <div>
            <strong>Thanks!</strong> Your responses are in. Join the discussion below.
          </div>
        </div>
        {chat.length === 0 && <div className="pt-chat-empty">No messages yet — say hello 👋</div>}
        {chat.map((m, i) => {
          const mine = m.author === myAuthor;
          const prev = chat[i - 1];
          const groupStart = !prev || prev.author !== m.author;
          return (
            <div key={m.id} className={`pt-msg ${mine ? "mine" : "theirs"}`}>
              {!mine && groupStart && (
                <div className="pt-msg-avatar" style={{ background: colorFor(m.author) }}>{initials(m.author)}</div>
              )}
              {!mine && !groupStart && <div className="pt-msg-avatar-spacer" />}
              <div className="pt-msg-body">
                {groupStart && (
                  <div className="pt-msg-meta">
                    {!mine && <span className="pt-msg-author">{m.author}</span>}
                    <span className="pt-msg-time">{clock(m.createdAt)}</span>
                  </div>
                )}
                <div className="pt-bubble">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div className="pt-composer">
        <input
          className="pt-composer-input"
          placeholder="Message…"
          value={chatInput}
          maxLength={600}
          enterKeyHint="send"
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendChat(); } }}
        />
        <button className="pt-send" onClick={sendChat} disabled={!chatInput.trim()} aria-label="Send">
          <svg viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        </button>
      </div>
    </div>
  );
}
